/**
 * Overlays built on React Aria (focus trap, Esc, focus restore, scroll lock, inert background).
 * Mobile uses BottomSheet; desktop uses Dialog/Drawer (APP_SHELL §2–3). Motion per MOTION_SYSTEM §3.
 */
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  Heading,
  Modal,
  ModalOverlay,
  OverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger,
  type ModalOverlayProps,
} from 'react-aria-components';
import { cx } from '../cx.ts';

const OVERLAY =
  'fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] ' +
  'data-[entering]:animate-[hx-fade-in_var(--hx-duration-base)_var(--hx-ease-standard)] ' +
  'data-[exiting]:animate-[hx-fade-out_var(--hx-duration-fast)_var(--hx-ease-standard)]';

interface OverlayProps extends Omit<ModalOverlayProps, 'children' | 'className'> {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Prevents dismissal while a command is in flight (e.g. confirmation submit). */
  isDismissDisabled?: boolean;
}

function CloseButton() {
  return (
    <AriaButton
      slot="close"
      aria-label="بستن"
      className="grid size-9 shrink-0 place-items-center rounded-full bg-surface-2 text-fg-muted hover:text-fg outline-hidden data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus cursor-pointer"
    >
      <X aria-hidden className="size-4" />
    </AriaButton>
  );
}

function Header({ title }: { title: string }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <Heading slot="title" className="m-0 text-base font-bold text-fg">
        {title}
      </Heading>
      <CloseButton />
    </div>
  );
}

/** Re-exported so apps compose overlays without depending on react-aria-components directly. */
export { DialogTrigger } from 'react-aria-components';

export function Dialog({ title, children, footer, isDismissDisabled, ...rest }: OverlayProps) {
  return (
    <ModalOverlay
      {...rest}
      isDismissable={!isDismissDisabled}
      isKeyboardDismissDisabled={isDismissDisabled}
      className={cx(OVERLAY, 'grid place-items-center p-4')}
    >
      <Modal className="w-full max-w-[480px] data-[entering]:animate-[hx-pop-in_var(--hx-duration-base)_var(--hx-ease-enter)]">
        {/* Never taller than the viewport: only the body scrolls, so the header and the footer (the
            confirmation button) stay reachable on short screens. Short dialogs render unchanged. */}
        <AriaDialog className="flex max-h-[calc(100dvh-2rem)] flex-col rounded-xl border border-line-strong bg-surface-2 p-6 shadow-float outline-hidden">
          <Header title={title} />
          <div className="-mx-1 min-h-0 overflow-y-auto px-1 text-sm leading-7 text-fg-muted">
            {children}
          </div>
          {footer && <div className="mt-6 flex shrink-0 flex-wrap justify-end gap-2">{footer}</div>}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

/**
 * Side panel. `end` (default, left in RTL) for details; `start` (right in RTL, the sidebar's edge) for
 * navigation on narrower viewports. The product is RTL-only, so the keyframes are written for RTL.
 */
export function Drawer({
  title,
  children,
  footer,
  isDismissDisabled,
  side = 'end',
  ...rest
}: OverlayProps & { side?: 'start' | 'end' }) {
  return (
    <ModalOverlay {...rest} isDismissable={!isDismissDisabled} className={OVERLAY}>
      <Modal
        className={cx(
          'fixed inset-y-0 w-full max-w-[440px] border-line-strong bg-surface-2 shadow-float',
          side === 'end'
            ? 'end-0 border-s data-[entering]:animate-[hx-drawer-in_var(--hx-duration-slow)_var(--hx-ease-enter)] data-[exiting]:animate-[hx-drawer-out_var(--hx-duration-base)_var(--hx-ease-standard)]'
            : 'start-0 max-w-[320px] border-e data-[entering]:animate-[hx-drawer-start-in_var(--hx-duration-slow)_var(--hx-ease-enter)] data-[exiting]:animate-[hx-drawer-start-out_var(--hx-duration-base)_var(--hx-ease-standard)]',
        )}
      >
        <AriaDialog className="flex h-full flex-col p-6 outline-hidden">
          <Header title={title} />
          <div className="min-h-0 flex-1 overflow-y-auto text-sm text-fg-muted">{children}</div>
          {footer && <div className="mt-4 flex gap-2 border-t border-line pt-4">{footer}</div>}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

/** Mobile bottom sheet: 26px top radius, handle, spring-like ease (prototype DNA). */
export function BottomSheet({ title, children, footer, isDismissDisabled, ...rest }: OverlayProps) {
  return (
    <ModalOverlay {...rest} isDismissable={!isDismissDisabled} className={OVERLAY}>
      <Modal
        className={cx(
          'fixed inset-x-0 bottom-0 max-h-[88dvh] rounded-t-sheet border-t border-line-strong bg-surface-2 shadow-float',
          'pb-[max(1.25rem,env(safe-area-inset-bottom))]',
          'data-[entering]:animate-[hx-sheet-in_var(--hx-duration-sheet)_var(--hx-ease-sheet)]',
          'data-[exiting]:animate-[hx-sheet-out_var(--hx-duration-base)_var(--hx-ease-standard)]',
        )}
      >
        <AriaDialog className="flex max-h-[88dvh] flex-col px-5 pt-2.5 outline-hidden">
          <span aria-hidden className="mx-auto mb-3.5 h-1 w-10 rounded-full bg-line-strong" />
          <Header title={title} />
          <div className="min-h-0 flex-1 overflow-y-auto text-sm text-fg-muted">{children}</div>
          {footer && <div className="mt-4 flex flex-col gap-2">{footer}</div>}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export function Tooltip({ content, children }: { content: string; children: ReactNode }) {
  return (
    <TooltipTrigger delay={350}>
      {children}
      <AriaTooltip
        offset={8}
        className="max-w-60 rounded-sm border border-line-strong bg-surface-3 px-2.5 py-1.5 text-xs text-fg shadow-float data-[entering]:animate-[hx-fade-in_var(--hx-duration-fast)]"
      >
        <OverlayArrow>
          <svg width={8} height={8} viewBox="0 0 8 8" className="fill-surface-3">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}
